import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import LoadingUiComponent from './components/loading/LoadingUiComponent';
import { loginLoader } from './apps/auth/query';
import Layout from './template/layout/Layout';

const OtpLoginPage = lazy(() => import('./page/login/OtpLoginPage'));
const DirectLoginPage = lazy(() => import('./page/login/DirectLoginPage'));
const AdminDashboard = lazy(
  () => import('./page/admin/dashboard/AdminDashboard')
);
const UsersList = lazy(() => import('./page/admin/usersList/UsersList'));
const NewOperatorPage = lazy(
  () => import('./page/admin/newUsers/NewOperatorPage')
);
const NewUserPage = lazy(() => import('./page/admin/newUsers/NewUserPage'));
const NewGameMasterPage = lazy(
  () => import('./page/admin/newUsers/NewGameMasterPage')
);
const NewCollectorPage = lazy(
  () => import('./page/admin/newUsers/NewCollectorPage')
);
const RolesPage = lazy(() => import('./page/admin/newUsers/RolesPage'));
const NotFoundPage = lazy(() => import('./page/notFound/NotFoundPage'));
const UserDetailsPage = lazy(
  () => import('./page/admin/usersList/UserDetailsPage')
);
const CollectorPage = lazy(
  () => import('./page/admin/collector/CollectorPage')
);
const CollectorDetailsPage = lazy(
  () => import('./page/admin/collector/CollectorDetailsPage')
);
const CollectorAddAndModifyPage = lazy(
  () => import('./page/admin/collector/CollectorAddAndModifyPage')
);
const OrdersListPage = lazy(() => import('./page/admin/orders/OrdersListPage'));
const OrdersDetailsPage = lazy(
  () => import('./page/admin/orders/OrdersDetailsPage')
);
const GamesListPage = lazy(() => import('./page/admin/games/GamesListPage'));
const GamesReservationListPage = lazy(
  () => import('./page/admin/games/GamesReservationListPage')
);
const DiscountListPage = lazy(
  () => import('./page/admin/discount/DiscountListPage')
);
const NewDiscountPage = lazy(
  () => import('./page/admin/discount/NewDiscountPage')
);
const ApiShopsPage = lazy(() => import('./page/admin/apiShops/ApiShopsPage'));
const ApiShopLogsPage = lazy(
  () => import('./page/admin/apiShops/ApiShopLogsPage')
);

const GameReservationDetailsPage = lazy(
  () => import('./page/admin/games/GameReservationDetailsPage')
);
const CreateAndModifyGamesPage = lazy(
  () => import('./page/admin/games/CreateAndModifyGamesPage')
);
const FinanceUsersPage = lazy(
  () => import('./page/admin/finance/FinanceUsersPage')
);
const FinanceUserDetailPage = lazy(
  () => import('./page/admin/finance/FinanceUserDetailPage')
);
const FinanceCollectorsPage = lazy(
  () => import('./page/admin/finance/FinanceCollectorsPage')
);
const FinanceDocumentsPage = lazy(
  () => import('./page/admin/finance/FinanceDocumentsPage')
);
const FinanceNewDocumentPage = lazy(
  () => import('./page/admin/finance/FinanceNewDocumentPage')
);
const FinanceCollectorDetailPage = lazy(
  () => import('./page/admin/finance/FinanceCollectorDetailPage')
);
const FinanceGameDetailsPage = lazy(
  () => import('./page/admin/finance/FinanceGameDetailsPage')
);

const CommentsPage = lazy(() => import('./page/admin/comments/CommentsPage'));
const AdminTicketsListPage = lazy(
  () => import('./page/admin/tickets/AdminTicketsListPage')
);
const AdminTicketDetailsPage = lazy(
  () => import('./page/admin/tickets/AdminTicketDetailsPage')
);
const AdminNewTicketPage = lazy(
  () => import('./page/admin/tickets/AdminNewTicketPage')
);
const AdminSmsListPage = lazy(
  () => import('./page/admin/sms/AdminSmsListPage')
);
const AdminSendSmsPage = lazy(
  () => import('./page/admin/sms/AdminSendSmsPage')
);
const AdminCitiesListPage = lazy(
  () => import('./page/admin/settings/AdminCitiesListPage')
);
const AdminUploadLogoPage = lazy(
  () => import('./page/admin/settings/AdminUploadLogoPage')
);

// Player
const PlayerCommentsPage = lazy(
  () => import('./page/player/comments/PlayerCommentsPage')
);

const PlayerDashboardPage = lazy(
  () => import('./page/player/dashboard/PlayerDashboardPage')
);
const PlayerProfilePage = lazy(
  () => import('./page/player/profile/PlayerProfilePage')
);
const PlayerProfileEditPage = lazy(
  () => import('./page/player/profile/PlayerProfileEditPage')
);
const PlayerFavoritsPage = lazy(
  () => import('./page/player/favorits/PlayerFavoritsPage')
);
const PlayerTicketsPage = lazy(
  () => import('./page/player/tickets/PlayerTicketsPage')
);
const PlayerNewTicketPage = lazy(
  () => import('./page/player/tickets/PlayerNewTicketPage')
);
const PlayerTicketDetailPage = lazy(
  () => import('./page/player/tickets/PlayerTicketDetailPage')
);
const PlayerGamesListPage = lazy(
  () => import('./page/player/games/PlayerGamesListPage')
);
const PlayerGameCommentsListPage = lazy(
  () => import('./page/player/games/PlayerGameCommentsListPage')
);
const PlayerNewCommentPage = lazy(
  () => import('./page/player/games/PlayerNewCommentPage')
);

// collector
const CollectorDashboardPage = lazy(
  () => import('./page/collector/dashboard/CollectorDashboardPage')
);
const CollectorProfilePage = lazy(
  () => import('./page/collector/profile/CollectorProfilePage')
);
const CollectorProfileEditPage = lazy(
  () => import('./page/collector/profile/CollectorProfileEditPage')
);
const CollectorGameMasterPage = lazy(
  () => import('./page/collector/gameMaster/CollectorGameMasterPage')
);
const CollectorGameReservationPage = lazy(
  () => import('./page/collector/games/CollectorGameReservationPage')
);
const CollectorNewGamePage = lazy(
  () => import('./page/collector/games/CollectorNewGamePage')
);
const CollectorReservationPage = lazy(
  () => import('./page/collector/reservation/CollectorReservationPage')
);
const CollectorFinanceListPage = lazy(
  () => import('./page/collector/finance/CollectorFinanceListPage')
);
const CollectorFinanceDetailsPage = lazy(
  () => import('./page/collector/finance/CollectorFinanceDetailsPage')
);
const CollectorCommentsPage = lazy(
  () => import('./page/collector/comments/CollectorCommentsPage')
);
const CollectorCommentDetailPage = lazy(
  () => import('./page/collector/comments/CollectorCommentDetailPage')
);
const CollectorTicketsListPage = lazy(
  () => import('./page/collector/tickets/CollectorTicketsListPage')
);
const CollectorNewTicketPage = lazy(
  () => import('./page/collector/tickets/CollectorNewTicketPage')
);
const CollectorTicketsDetailsPage = lazy(
  () => import('./page/collector/tickets/CollectorTicketsDetailsPage')
);
const CollectorDiscountListPage = lazy(
  () => import('./page/collector/discount/CollectorDiscountListPage')
);
const CollectorNewDiscountPage = lazy(
  () => import('./page/collector/discount/CollectorNewDiscountPage')
);
const CollectorSmsListPage = lazy(
  () => import('./page/collector/sms/CollectorSmsListPage')
);
const CollectorNewSmsPage = lazy(
  () => import('./page/collector/sms/CollectorNewSmsPage')
);

// operator
const OperatorDashboardPage = lazy(
  () => import('./page/operator/dashboard/OperatorDashboardPage')
);
const OperatorNewPlayerPage = lazy(
  () => import('./page/operator/newUser/OperatorNewPlayerPage')
);
const OperatorNewGameMasterPage = lazy(
  () => import('./page/operator/newUser/OperatorNewGameMasterPage')
);
const OperatorNewCollectorPage = lazy(
  () => import('./page/operator/newUser/OperatorNewCollectorPage')
);
const OperatorRolesPage = lazy(
  () => import('./page/operator/newUser/OperatorRolesPage')
);
const OperatorUserDetailsPage = lazy(
  () => import('./page/operator/usersList/OperatorUserDetailsPage')
);
const OperatorUsersListPage = lazy(
  () => import('./page/operator/usersList/OperatorUsersListPage')
);

const OperatorSmsListPage = lazy(
  () => import('./page/operator/sms/OperatorSmsListPage')
);
const OperatorNewSmsPage = lazy(
  () => import('./page/operator/sms/OperatorNewSmsPage')
);
const GameMasterDashboardPage = lazy(
  () => import('./page/gameMaster/dashboard/GameMasterDashboardPage')
);
const GameMasterReservationPage = lazy(
  () => import('./page/gameMaster/reservation/GameMasterReservationPage')
);
const GameMasterTicketsListPage = lazy(
  () => import('./page/gameMaster/tickets/GameMasterTicketsListPage')
);
const GameMasterTicketDetailsPage = lazy(
  () => import('./page/gameMaster/tickets/GameMasterTicketDetailsPage')
);
const GameMasterNewTicketPage = lazy(
  () => import('./page/gameMaster/tickets/GameMasterNewTicketPage')
);
const GameMasterSmsListPage = lazy(
  () => import('./page/gameMaster/sms/GameMasterSmsListPage')
);
const GameMasterNewSmsPage = lazy(
  () => import('./page/gameMaster/sms/GameMasterNewSmsPage')
);
const ReservationDashboardPage = lazy(
  () => import('./page/reservation/ReservationDashboardPage')
);
const PublicPlayerPage = lazy(
  () => import('./page/public/PublicPlayerPage')
);

type LayoutTypes = {
  children: JSX.Element;
};

const RouterLayout = ({ children }: LayoutTypes): JSX.Element => {
  return (
    <>
      <Layout>
        <Suspense fallback={<LoadingUiComponent />}>{children}</Suspense>
      </Layout>
    </>
  );
};

export const routes = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RouterLayout>
        <OtpLoginPage />
      </RouterLayout>
    ),
    loader: () => loginLoader(),
  },
  {
    path: '/login/direct',
    element: (
      <RouterLayout>
        <DirectLoginPage />
      </RouterLayout>
    ),
    loader: () => loginLoader(),
  },
  {
    path: '/admin/dashboard',
    element: (
      <RouterLayout>
        <AdminDashboard />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/list',
    element: (
      <RouterLayout>
        <UsersList />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/show/:id',
    element: (
      <RouterLayout>
        <UserDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/new',
    element: (
      <RouterLayout>
        <RolesPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/new/operator/:id?',
    element: (
      <RouterLayout>
        <NewOperatorPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/new/user/:id?',
    element: (
      <RouterLayout>
        <NewUserPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/new/gamemaster/:id?',
    element: (
      <RouterLayout>
        <NewGameMasterPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/user/new/collector/:id?',
    element: (
      <RouterLayout>
        <NewCollectorPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/collector',
    element: (
      <RouterLayout>
        <CollectorPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/collector/show/:id',
    element: (
      <RouterLayout>
        <CollectorDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/collector/new/:id',
    element: (
      <RouterLayout>
        <CollectorAddAndModifyPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/orders',
    element: (
      <RouterLayout>
        <OrdersListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/orders/:id',
    element: (
      <RouterLayout>
        <OrdersDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/games/list',
    element: (
      <RouterLayout>
        <GamesListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/games/reserves',
    element: (
      <RouterLayout>
        <GamesReservationListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/games/new/:id?',
    element: (
      <RouterLayout>
        <CreateAndModifyGamesPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/users',
    element: (
      <RouterLayout>
        <FinanceUsersPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/users/:id',
    element: (
      <RouterLayout>
        <FinanceUserDetailPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/collectors',
    element: (
      <RouterLayout>
        <FinanceCollectorsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/collectors/:id',
    element: (
      <RouterLayout>
        <FinanceCollectorDetailPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/games/:id',
    element: (
      <RouterLayout>
        <FinanceGameDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/documents',
    element: (
      <RouterLayout>
        <FinanceDocumentsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/financial/documents/new/:id?',
    element: (
      <RouterLayout>
        <FinanceNewDocumentPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/comments',
    element: (
      <RouterLayout>
        <CommentsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/tickets',
    element: (
      <RouterLayout>
        <AdminTicketsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/tickets/:id',
    element: (
      <RouterLayout>
        <AdminTicketDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/tickets/new',
    element: (
      <RouterLayout>
        <AdminNewTicketPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/sms/list',
    element: (
      <RouterLayout>
        <AdminSmsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/sms/new',
    element: (
      <RouterLayout>
        <AdminSendSmsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/discount',
    element: (
      <RouterLayout>
        <DiscountListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/api-shops',
    element: (
      <RouterLayout>
        <ApiShopsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/api-shops/logs',
    element: (
      <RouterLayout>
        <ApiShopLogsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/discount/new/:id?',
    element: (
      <RouterLayout>
        <NewDiscountPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/game/reserves/show/:id',
    element: (
      <RouterLayout>
        <GameReservationDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/settings/cities',
    element: (
      <RouterLayout>
        <AdminCitiesListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/admin/settings/logo',
    element: (
      <RouterLayout>
        <AdminUploadLogoPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('admin'),
  },
  {
    path: '/user/dashboard',
    element: (
      <RouterLayout>
        <PlayerDashboardPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/profile',
    element: (
      <RouterLayout>
        <PlayerProfilePage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/profile/edit',
    element: (
      <RouterLayout>
        <PlayerProfileEditPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/comments',
    element: (
      <RouterLayout>
        <PlayerCommentsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/favorites',
    element: (
      <RouterLayout>
        <PlayerFavoritsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/tickets',
    element: (
      <RouterLayout>
        <PlayerTicketsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/tickets/new',
    element: (
      <RouterLayout>
        <PlayerNewTicketPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/tickets/:id',
    element: (
      <RouterLayout>
        <PlayerTicketDetailPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/games/list',
    element: (
      <RouterLayout>
        <PlayerGamesListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/game/comment/:id',
    element: (
      <RouterLayout>
        <PlayerGameCommentsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/user/games/comment/new/:id',
    element: (
      <RouterLayout>
        <PlayerNewCommentPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('user'),
  },
  {
    path: '/collector/dashboard',
    element: (
      <RouterLayout>
        <CollectorDashboardPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/profile',
    element: (
      <RouterLayout>
        <CollectorProfilePage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/profile/edit/:id',
    element: (
      <RouterLayout>
        <CollectorProfileEditPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/gamemaster',
    element: (
      <RouterLayout>
        <CollectorGameMasterPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/games/reservation',
    element: (
      <RouterLayout>
        <CollectorGameReservationPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/games/new/:id?',
    element: (
      <RouterLayout>
        <CollectorNewGamePage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/reservations',
    element: (
      <RouterLayout>
        <CollectorReservationPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/finance/list',
    element: (
      <RouterLayout>
        <CollectorFinanceListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/finance/:id',
    element: (
      <RouterLayout>
        <CollectorFinanceDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/comments',
    element: (
      <RouterLayout>
        <CollectorCommentsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/comments/:id',
    element: (
      <RouterLayout>
        <CollectorCommentDetailPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/tickets',
    element: (
      <RouterLayout>
        <CollectorTicketsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/tickets/new',
    element: (
      <RouterLayout>
        <CollectorNewTicketPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/tickets/:id',
    element: (
      <RouterLayout>
        <CollectorTicketsDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/discounts',
    element: (
      <RouterLayout>
        <CollectorDiscountListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/discount/new/:id?',
    element: (
      <RouterLayout>
        <CollectorNewDiscountPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/sms/list',
    element: (
      <RouterLayout>
        <CollectorSmsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/collector/sms/new',
    element: (
      <RouterLayout>
        <CollectorNewSmsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('collector'),
  },
  {
    path: '/operator/dashboard',
    element: (
      <RouterLayout>
        <OperatorDashboardPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/user/new',
    element: (
      <RouterLayout>
        <OperatorRolesPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/user/new/user/:id?',
    element: (
      <RouterLayout>
        <OperatorNewPlayerPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/user/new/gamemaster/:id?',
    element: (
      <RouterLayout>
        <OperatorNewGameMasterPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/user/new/collector/:id?',
    element: (
      <RouterLayout>
        <OperatorNewCollectorPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/user/list',
    element: (
      <RouterLayout>
        <OperatorUsersListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/user/show/:id',
    element: (
      <RouterLayout>
        <OperatorUserDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/collector',
    element: (
      <RouterLayout>
        <CollectorPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/collector/show/:id',
    element: (
      <RouterLayout>
        <CollectorDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/collector/new/:id',
    element: (
      <RouterLayout>
        <CollectorAddAndModifyPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/games/list',
    element: (
      <RouterLayout>
        <GamesListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/games/reserves',
    element: (
      <RouterLayout>
        <GamesReservationListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/game/reserves/show/:id',
    element: (
      <RouterLayout>
        <GameReservationDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/games/new/:id?',
    element: (
      <RouterLayout>
        <CreateAndModifyGamesPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/orders',
    element: (
      <RouterLayout>
        <OrdersListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/orders/:id',
    element: (
      <RouterLayout>
        <OrdersDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/users',
    element: (
      <RouterLayout>
        <FinanceUsersPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/users/:id',
    element: (
      <RouterLayout>
        <FinanceUserDetailPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/collectors',
    element: (
      <RouterLayout>
        <FinanceCollectorsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/collectors/:id',
    element: (
      <RouterLayout>
        <FinanceCollectorDetailPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/games/:id',
    element: (
      <RouterLayout>
        <FinanceGameDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/documents',
    element: (
      <RouterLayout>
        <FinanceDocumentsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/financial/documents/new/:id?',
    element: (
      <RouterLayout>
        <FinanceNewDocumentPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/comments',
    element: (
      <RouterLayout>
        <CommentsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/tickets',
    element: (
      <RouterLayout>
        <AdminTicketsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/tickets/:id',
    element: (
      <RouterLayout>
        <AdminTicketDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/tickets/new',
    element: (
      <RouterLayout>
        <AdminNewTicketPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/sms/list',
    element: (
      <RouterLayout>
        <OperatorSmsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/sms/new',
    element: (
      <RouterLayout>
        <OperatorNewSmsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/discount',
    element: (
      <RouterLayout>
        <DiscountListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/operator/discount/new/:id?',
    element: (
      <RouterLayout>
        <NewDiscountPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('operator'),
  },
  {
    path: '/assist/dashboard',
    element: (
      <RouterLayout>
        <GameMasterDashboardPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/assist/reservations',
    element: (
      <RouterLayout>
        <GameMasterReservationPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/assist/tickets',
    element: (
      <RouterLayout>
        <GameMasterTicketsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/assist/tickets/:id',
    element: (
      <RouterLayout>
        <GameMasterTicketDetailsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/assist/tickets/new',
    element: (
      <RouterLayout>
        <GameMasterNewTicketPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/assist/sms/list',
    element: (
      <RouterLayout>
        <GameMasterSmsListPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/assist/sms/new',
    element: (
      <RouterLayout>
        <GameMasterNewSmsPage />
      </RouterLayout>
    ),
    loader: () => loginLoader('assist'),
  },
  {
    path: '/reservation/:id',
    element: (
      <RouterLayout>
        <ReservationDashboardPage />
      </RouterLayout>
    ),
  },
  {
    path: '/public/player/:id',
    element: (
      <RouterLayout>
        <PublicPlayerPage />
      </RouterLayout>
    ),
  },
  {
    path: 'notfound',
    element: (
      <RouterLayout>
        <NotFoundPage />
      </RouterLayout>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/notfound" replace />,
  },
]);
